import { SOURCE_PAGE } from '@chegg-wt/core/constants/citationFlow'
import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { AppState } from '@chegg-wt/core/state'
import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import { MainWrapper } from '@chegg-wt/core/components/Layouts/styled'
import { BreadcrumbPart, Breadcrumbs } from '@chegg-wt/core/components/Breadcrumbs'

import BreadcrumbData from '@chegg-wt/core/components/Head/BreadcrumbData'
import sourceTypes from '@chegg-wt/core/constants/sourceTypes'
import Credibility from '@chegg-wt/core/components/Credibility'
import ErrorContainer from '@chegg-wt/core/components/ErrorContainer'
import Resources from '@chegg-wt/core/components/Resources'
import SeoContent from '@chegg-wt/core/components/SeoContent'
import SourceTypeLinkPicker from '@chegg-wt/core/components/SourceTypeLinkPicker'
import UpgradeAdFrame from '@chegg-wt/core/components/UpgradeAdFrame'
import CreateAccountBanner from '@chegg-wt/core/components/CreateAccountBanner'
import { AppRoute, SourceType } from '@chegg-wt/core/types/core'
import { sourceTypeSelected } from '@chegg-wt/core/state/createCitation/actions'
import { fetchUrlStyleName } from '@chegg-wt/core/state/currentProject/actions'
import {
  currentStyleSelector,
  currentUrlStyleNameSelector,
} from '@chegg-wt/core/state/currentProject/selectors'
import {
  SourceTypeSelectionDispatchProps,
  SourceTypeSelectionProps,
  SourceTypeSelectionStateProps,
} from './types'
import * as Styled from './styled'

const SourceTypeSelection: React.FC<SourceTypeSelectionProps> = ({
  styleId,
  urlStyleName,
  initialUrlStyleName,
  currentStyle: style,
  selectSourceType,
  urlStyleNameFetched,
  copy = {},
}) => {
  const { toSourceSearch, toManualCitation } = useRoutesContext()
  const routes = useRoutesContext()
  const dropdownSourceTypes = [...sourceTypes]
  const buttonSourceTypes = dropdownSourceTypes.splice(0, 3)

  const seoOrCredibilityResourcesContent = copy.seoContent ? (
    <SeoContent content={copy.seoContent} advertPlacement="C2" />
  ) : (
    <>
      {!!copy.considerCredibility && (
        <Credibility copy={copy.considerCredibility} advertPlacement="C2" />
      )}
      {!!copy.resources && <Resources copy={copy.resources} />}
    </>
  )

  const hasBreadcrumbs =
    typeof styleId === 'string' && typeof initialUrlStyleName === 'string'

  useEffect(() => {
    if (hasBreadcrumbs) urlStyleNameFetched(styleId)
  }, [styleId])

  const breadcrumbs: BreadcrumbPart[] = [
    {
      label: 'Home',
      route: routes.toCitationsHome(),
    },
    {
      label: `${urlStyleName || initialUrlStyleName} Citation Generator`,
      route: routes.toSourceTypeSelection({ style: styleId }),
      last: true,
    },
  ]

  const onSourceTypeSelect = ({ id: sourceType }: SourceType) => {
    selectSourceType(sourceType, 'toSourceSearch')
  }

  const composeSourceTypeLink = (sourceType: SourceType): AppRoute => {
    const routeParams = { style, sourceType: sourceType.id }
    if (sourceType.manual) {
      return toManualCitation({ ...routeParams })
    }
    return toSourceSearch({ ...routeParams })
  }

  return (
    <>
      {hasBreadcrumbs ? <BreadcrumbData breadcrumbs={breadcrumbs} /> : null}
      <Styled.PageContainer>
        <UpgradeAdFrame
          copy={copy?.upgradeCopy}
          page={SOURCE_PAGE}
          upgradePromotionCopy={copy?.upgradePromotionCopy}
        >
          <ErrorContainer />
          <MainWrapper>
            <Styled.Box>
              <SourceTypeLinkPicker
                buttonSourceTypes={buttonSourceTypes}
                dropdownSourceTypes={dropdownSourceTypes}
                composeRoute={composeSourceTypeLink}
                onSourceTypeSelect={onSourceTypeSelect}
              />
            </Styled.Box>
          </MainWrapper>
          <CreateAccountBanner />
        </UpgradeAdFrame>
        {seoOrCredibilityResourcesContent}
        {hasBreadcrumbs ? <Breadcrumbs parts={breadcrumbs} divider=">" /> : null}
      </Styled.PageContainer>
    </>
  )
}

const mapStateToProps = (state: AppState) => ({
  currentStyle: currentStyleSelector(state),
  urlStyleName: currentUrlStyleNameSelector(state),
})

const mapDispatchToProps = {
  selectSourceType: sourceTypeSelected,
  urlStyleNameFetched: fetchUrlStyleName,
}

export default connect<SourceTypeSelectionStateProps, SourceTypeSelectionDispatchProps>(
  mapStateToProps,
  mapDispatchToProps
)(SourceTypeSelection)
